import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Turning agent and contact text into safe HTML.
 *
 * Every chat in the product is rich — markdown, tables, code, diagrams, emoji — and
 * all of it arrives as text we did not write. So sanitisation is not a precaution
 * here, it is the contract: an agent reply can contain anything a web page it read
 * contained, and a customer message is straightforwardly hostile input.
 *
 * Sanitising happens ONCE, after markdown, and with a per-origin policy. Two
 * origins, two threat models:
 *
 *   - `agent` — our own model's output. Rich: tables, images, links, code.
 *   - `contact` — a stranger on WhatsApp. Text and emphasis only. No images (a
 *     remote image is a read receipt and an IP leak), no links rendered as links.
 *
 * V2 learned the second one the hard way: rendering contact content with the same
 * generosity as agent content turns the inbox into a tracking surface.
 */
export type RichOrigin = "agent" | "contact";

const AGENT_TAGS = [
	"p",
	"br",
	"strong",
	"em",
	"del",
	"code",
	"pre",
	"blockquote",
	"ul",
	"ol",
	"li",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"a",
	"img",
	"table",
	"thead",
	"tbody",
	"tr",
	"th",
	"td",
	"hr",
	"span",
];

const CONTACT_TAGS = [
	"p",
	"br",
	"strong",
	"em",
	"del",
	"code",
	"pre",
	"blockquote",
	"ul",
	"ol",
	"li",
];

marked.setOptions({ gfm: true, breaks: true });

/**
 * TODO LINK ABRE NOUTRA ABA — decisão do dono (08/09).
 *
 * O painel é um lugar de trabalho: um link do Ben (um arquivo puxado, um site, um
 * formulário `/f/…`, um documento `/d/…`) que navegasse a MESMA aba tirava a pessoa
 * do meio de uma conversa com o assistente — e o botão "voltar" não devolve um
 * turno em curso. `target`/`rel` já eram atributos PERMITIDOS pelo sanitizador;
 * faltava alguém pô-los. Aqui, e não num `renderer` do marked: o gancho corre
 * DEPOIS da sanitização, em todo `<a>` que sobreviveu, então nada que o marked
 * ou o próprio texto escreva consegue chegar à tela sem ele. `noopener
 * noreferrer` porque a aba nova nunca deve ter a mão na nossa (`window.opener`).
 * Âncoras internas (`#…`) ficam na página: são o índice de um documento longo.
 */
let linksHooked = false;

/**
 * Registado na PRIMEIRA sanitização e não no topo do módulo: sem `window` (um
 * spec em ambiente Node que só importa este ficheiro) o DOMPurify nasce sem
 * `addHook`, e um `import` que rebenta derruba todo teste que passe por aqui.
 * Onde há o que sanitizar há DOM — é aí que o gancho faz sentido.
 */
function hookLinksToNewTab(): void {
	if (linksHooked || typeof DOMPurify.addHook !== "function") return;
	DOMPurify.addHook("afterSanitizeAttributes", (node) => {
		if (node.tagName !== "A") return;
		const href = node.getAttribute("href") ?? "";
		if (!href || href.startsWith("#")) return;
		node.setAttribute("target", "_blank");
		node.setAttribute("rel", "noopener noreferrer");
	});
	linksHooked = true;
}

export interface RichBlock {
	readonly kind: "html" | "mermaid";
	/** Sanitised HTML, or the raw diagram source when `kind` is mermaid. */
	readonly content: string;
}

/**
 * Splits text into renderable blocks, pulling mermaid out of the markdown.
 *
 * Mermaid has to be separated BEFORE markdown runs: as a fenced code block it would
 * become escaped `<pre>` text, and mermaid's own parser needs the original source.
 * Returning blocks rather than one HTML string also keeps the heavy mermaid bundle
 * off the page entirely when no diagram is present.
 */
export function toRichBlocks(text: string, origin: RichOrigin = "agent"): RichBlock[] {
	if (!text) return [];

	// A stranger does not get to draw diagrams in our UI: mermaid executes its own
	// parser on the input and renders SVG, which is a much larger surface than text.
	if (origin === "contact") {
		return [{ kind: "html", content: toSafeHtml(text, origin) }];
	}

	const blocks: RichBlock[] = [];
	const fence = /```mermaid\s*\n([\s\S]*?)```/g;
	let cursor = 0;

	for (const match of text.matchAll(fence)) {
		const before = text.slice(cursor, match.index);
		if (before.trim()) blocks.push({ kind: "html", content: toSafeHtml(before, origin) });
		const diagram = (match[1] ?? "").trim();
		if (diagram) blocks.push({ kind: "mermaid", content: diagram });
		cursor = (match.index ?? 0) + match[0].length;
	}

	const rest = text.slice(cursor);
	if (rest.trim()) blocks.push({ kind: "html", content: toSafeHtml(rest, origin) });

	return blocks.length > 0 ? blocks : [{ kind: "html", content: toSafeHtml(text, origin) }];
}

export function toSafeHtml(markdown: string, origin: RichOrigin = "agent"): string {
	hookLinksToNewTab();
	const html = marked.parse(markdown, { async: false }) as string;

	const clean = DOMPurify.sanitize(html, {
		ALLOWED_TAGS: origin === "agent" ? AGENT_TAGS : CONTACT_TAGS,
		// `class` is allowed so the table wrapper below survives; DOMPurify would
		// otherwise strip the very attribute the stylesheet keys off.
		ALLOWED_ATTR:
			origin === "agent" ? ["href", "title", "src", "alt", "target", "rel", "class"] : [],
		// `javascript:` and `data:` in an href are the classic markdown-link payload.
		ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
		// Rendered into a component's element, not a document.
		FORBID_TAGS: ["style", "script", "iframe", "form", "input", "object", "embed"],
		FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
	});

	return wrapCode(wrapTables(clean));
}

/**
 * Puts every table inside its own horizontal scroller.
 *
 * Without a wrapper the table is both the scroll box and the layout box, and it
 * resolves that conflict by crushing columns instead of scrolling — a header reading
 * "Cód igo" over a one-character-wide column. With a wrapper the table takes its
 * natural width and the wrapper scrolls.
 *
 * Runs AFTER sanitisation and only adds markup of our own, so it introduces nothing
 * the sanitiser has not already seen.
 */
function wrapTables(html: string): string {
	if (!html.includes("<table")) return html;
	return html
		.replace(/<table/g, '<div class="rich-table-scroll"><table')
		.replace(/<\/table>/g, "</table></div>");
}

/**
 * Puts a TOOLBAR on every code block: the language, wrap, and copy.
 *
 * An agent that answers with a command, a snippet or a query has produced
 * something meant to be USED, and selecting it by hand out of a scrolling box on
 * a phone is where that value is lost.
 *
 * Three parts, and each one earns its pixels:
 *
 *   - the LANGUAGE, read off the `language-*` class marked already emitted. It is
 *     the difference between a shell command and a SQL statement at a glance, and
 *     the fence already carried it — it was simply thrown away.
 *   - WRAP, because a 200-character line in a 24rem dock is a horizontal scroll
 *     that hides the end of the very line being read. V2's block had this button
 *     for exactly that reason.
 *   - COPY, which was already here.
 *
 * The buttons carry no payload: the handler reads the text out of the `<pre>`
 * inside the same block at click time. Encoding the source into an attribute
 * would put model output back into markup after the sanitiser has already seen
 * it, which is the exact ordering that makes an escaping bug exploitable. The
 * language is the one thing read back OUT of the markup, and the pattern that
 * reads it accepts letters, digits and four punctuation marks only.
 *
 * Runs AFTER sanitisation and adds only markup of our own.
 */
function wrapCode(html: string): string {
	if (!html.includes("<pre")) return html;
	const opened = html.replace(/<pre\b[^>]*>(?:\s*<code\b[^>]*>)?/g, (match) => {
		const language = /class="[^"]*language-([\w+#.-]+)/.exec(match)?.[1] ?? "";
		return `<div class="rich-code">${codeToolbar(language)}${match}`;
	});
	return opened.replace(/<\/pre>/g, "</pre></div>");
}

function codeToolbar(language: string): string {
	const label = language ? `<span class="rich-code__lang">${language}</span>` : "";
	return `<div class="rich-code__bar">${label}<button type="button" class="rich-code__btn" data-rich-wrap>quebrar</button><button type="button" class="rich-code__btn" data-rich-copy>copiar</button></div>`;
}

/**
 * Whether a block is a DRAWING made of characters, rather than code.
 *
 * The difference decides what a phone should do with it. A long line of SQL that
 * runs off the right edge is still readable line by line, and scrolling to the end
 * of the one line being read costs nothing. A tree drawn with `├─` is the opposite:
 * cut it in half and it stops being a tree — the branches on the right, which say
 * what the thing actually contains, are simply not there. Ben answering "the
 * inventory of this workspace" with a box diagram on a 390px screen showed the
 * left half of every label and nothing else.
 *
 * So art gets SHRUNK to fit (`RichText`) and code keeps its scroll.
 *
 * Detected by the characters only a drawing uses — box drawing (U+2500–257F),
 * blocks and geometric shapes (U+2580–25FF) — and only from the second line on: a
 * single line with an arrow in it is a sentence, not a diagram.
 */
export function isFixedWidthArt(code: string): boolean {
	if (!/[─-◿]/.test(code)) return false;
	return code.trimEnd().includes("\n");
}

/**
 * A one-line preview, for a list where the full render would be noise.
 *
 * Strips markdown structure rather than rendering it: a conversation list showing a
 * table cell border is worse than showing plain text.
 */
export function toPreview(text: string, limit = 120): string {
	const flat = text
		.replace(/```[\s\S]*?```/g, " [code] ")
		.replace(/!\[[^\]]*\]\([^)]*\)/g, " [image] ")
		.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
		.replace(/[*_`>#|-]/g, "")
		.replace(/\s+/g, " ")
		.trim();
	return flat.length > limit ? `${flat.slice(0, limit - 1)}…` : flat;
}
